/**
 * The base query. Every request in both apps goes through here.
 *
 * Responsibilities, in order: attach credentials, refresh a stale access token exactly once
 * across concurrent callers, recover from plain expiry without a hard logout (`recoverSession`),
 * translate RFC 7807 into the `ApiError` the UI reads, and turn a real revocation into a wipe
 * rather than a login loop.
 */

import {
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  type FetchBaseQueryMeta,
  type QueryReturnValue,
} from '@reduxjs/toolkit/query';

import { deviceId } from '../auth/device.js';
import { csrfToken, getAccessToken, isAccessTokenExpiring, setAccessToken } from '../auth/tokenStore.js';
import { uuidv7 } from '../lib/uuid.js';
import { ErrorCode, FALLBACK_MESSAGE, type ApiError, type ProblemDetails } from '../types/errors.js';

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export type PosQueryArgs = (FetchArgs | string) & {
  /**
   * Supplied by the caller when the key must survive a retry — a sale generates its key before
   * the user taps Complete, so a resubmission is the same key and the server returns the
   * original sale instead of a second one.
   */
  idempotencyKey?: string;
  /**
   * Renew the session before sending, even though the token is not yet inside its refresh skew.
   * Only `keepSessionAlive` sets it: the person is using the page but nothing on it has called
   * the server, and without this the token runs out under them.
   */
  renewFirst?: boolean;
};

/** What the app is told when the session ends. The app decides what to do about it. */
export type SessionEnded = (code: string, message: string) => void;

export type BaseQueryOptions = {
  /**
   * A getter, not a string: the API instance is a module singleton created at import time, and
   * the app configures its base URL from the environment after that. Resolved on first request.
   */
  readonly baseUrl: () => string;
  /**
   * A getter for the same reason as `baseUrl` — `configureApi` sets the channel after this
   * instance already exists. `recoverSession` reads it per attempt, never caches it, so it is
   * always the channel the app actually configured itself as.
   */
  readonly channel: () => 'pos' | 'dashboard' | null;
  readonly onSessionEnded: SessionEnded;
  /**
   * POS only: the access token expired locally — a backgrounded tab woken up, not a revocation —
   * and the fix is the PIN screen, not a wipe. Dashboard never calls this; it recovers the same
   * case silently through `/auth/continue` instead. See `recoverSession`.
   */
  readonly onNeedsUnlock: () => void;
};

const raw = (baseUrl: string) =>
  fetchBaseQuery({
    baseUrl,
    // The refresh token is an HttpOnly cookie; without this it is never sent and every renew
    // fails with no visible cause.
    credentials: 'include',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token !== null) headers.set('Authorization', `Bearer ${token}`);

      headers.set('X-Device-Id', deviceId());

      // Sent on every request, not only on RTK Query mutations. The middleware checks the token
      // by HTTP method, and two POSTs are not mutations from RTK Query's point of view: the
      // pre-emptive `/auth/renew` below, which inherits the `type` of whichever query triggered
      // it, and `duplicate-check`, which is a `build.query` that posts a body. Gating on `type`
      // sent both without the header and both came back 403 CSRF_MISMATCH — a session dying
      // mid-use for a reason that had nothing to do with the session. A token on a GET costs
      // fifty bytes and cannot be gated wrong.
      const csrf = csrfToken();
      if (csrf !== null) headers.set('X-CSRF-Token', csrf);

      return headers;
    },
  });

/**
 * One refresh in flight at a time. Six components mounting at once produce six 401s; without
 * this they produce six renews, five of which rotate a token the other five are still using.
 */
let refreshing: Promise<ApiError | null> | null = null;

/** Revocation-family codes. A real end, whichever request surfaces them. */
const REVOCATION_CODES: readonly string[] = [
  ErrorCode.DeviceRevoked,
  ErrorCode.RefreshExpired,
  ErrorCode.SignedInElsewhere,
  ErrorCode.DeviceRevokedByOwner,
  ErrorCode.PinAttemptsExceeded,
  ErrorCode.PasswordChanged,
  ErrorCode.PasswordAttemptsExceeded,
];

export function createBaseQuery({
  baseUrl,
  channel,
  onSessionEnded,
  onNeedsUnlock,
}: BaseQueryOptions): BaseQueryFn<PosQueryArgs, unknown, ApiError> {
  let cached: ReturnType<typeof raw> | null = null;
  const resolve = (): ReturnType<typeof raw> => (cached ??= raw(baseUrl()));

  return async (args, api, extraOptions) => {
    const inner = resolve();
    const renewFirst = typeof args === 'object' && args.renewFirst === true;
    const request = normalise(args);

    // Refresh *before* sending rather than after a 401, so a slow request does not fail on a
    // token that expired while it was in flight.
    if ((isAccessTokenExpiring() && !isAuthRoute(request)) || renewFirst) {
      const recoveryFailure = await recoverSession(inner, api, extraOptions, channel, onSessionEnded, onNeedsUnlock);
      if (recoveryFailure !== null) return { error: recoveryFailure };
    }

    let result = await inner(request, api, extraOptions);

    // A 401 that survives a pre-emptive refresh means the server disagrees about the session.
    if (result.error?.status === 401 && !isAuthRoute(request)) {
      const recoveryFailure = await recoverSession(inner, api, extraOptions, channel, onSessionEnded, onNeedsUnlock);
      if (recoveryFailure === null) result = await inner(request, api, extraOptions);
    }

    if (result.error) {
      const error = toApiError(result.error);

      if (REVOCATION_CODES.includes(error.code)) {
        endSessionAfterThisRequestSettles(onSessionEnded, error.code, error.message);
      }

      return { error };
    }

    return { data: result.data };
  };
}

/**
 * `onSessionEnded` dispatches `resetApiState()`, and that has a side effect that is easy to
 * miss: RTK Query's own middleware reacts to it by aborting every mutation it still considers
 * "running" — including the one calling this, because the base query function has not returned
 * yet at this point. That abort used to race the base query's own return value inside
 * `createAsyncThunk`'s internals, and the abort usually won: the caller received a bare
 * `AbortError` instead of the `DEVICE_REVOKED`/`REFRESH_EXPIRED` result already computed for it —
 * confirmed live, not theorised: `P1-008`'s `DEVICE_REVOKED` response arrived over the wire fine
 * and still surfaced as `{name: 'AbortError'}` in the component's catch block. A macrotask
 * defers the dispatch until after the request has fully settled and is no longer "running" from
 * RTK Query's point of view, so the abort has nothing left to catch.
 */
function endSessionAfterThisRequestSettles(
  onSessionEnded: SessionEnded,
  code: string,
  message: string,
): void {
  setTimeout(() => {
    onSessionEnded(code, message);
  }, 0);
}

function normalise(args: PosQueryArgs): FetchArgs {
  const request: FetchArgs = typeof args === 'string' ? { url: args } : withoutLocalFlags(args);
  const method = (request.method ?? 'GET').toUpperCase();

  if (WRITE_METHODS.has(method)) {
    // Rule from phase 1: every write carries an idempotency key. Generated here when the caller
    // did not supply one, so a mutation cannot ship without it by omission.
    const key = typeof args === 'object' ? (args.idempotencyKey ?? uuidv7()) : uuidv7();
    request.headers = { ...(request.headers as Record<string, string> | undefined), 'Idempotency-Key': key };
  }

  return request;
}

/** The flags only this file reads; they are not part of the HTTP request. */
function withoutLocalFlags(args: FetchArgs & { idempotencyKey?: string; renewFirst?: boolean }): FetchArgs {
  const { renewFirst: _renewFirst, ...request } = args;
  return request;
}

const isAuthRoute = (request: FetchArgs): boolean =>
  typeof request.url === 'string' && request.url.startsWith('/auth/');

/** `null` on success. Otherwise the server's own reason — never re-derived from the code alone. */
async function refreshOnce(
  inner: ReturnType<typeof raw>,
  api: Parameters<ReturnType<typeof raw>>[1],
  extraOptions: Parameters<ReturnType<typeof raw>>[2],
): Promise<ApiError | null> {
  refreshing ??= (async () => {
    try {
      const result = await inner({ url: '/auth/renew', method: 'POST' }, api, extraOptions);
      return applySession(result);
    } finally {
      // Cleared in `finally` so a thrown renew does not wedge every later request behind a
      // promise that already settled.
      refreshing = null;
    }
  })();

  return refreshing;
}

/**
 * One `/auth/continue` in flight at a time, mirroring `refreshing` — it rotates the refresh
 * token exactly like renew does, so two concurrent callers must not both spend it.
 */
let continuing: Promise<ApiError | null> | null = null;

async function continueOnce(
  inner: ReturnType<typeof raw>,
  api: Parameters<ReturnType<typeof raw>>[1],
  extraOptions: Parameters<ReturnType<typeof raw>>[2],
): Promise<ApiError | null> {
  continuing ??= (async () => {
    try {
      const result = await inner({ url: '/auth/continue', method: 'POST' }, api, extraOptions);
      return applySession(result);
    } finally {
      continuing = null;
    }
  })();

  return continuing;
}

function applySession(
  result: QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta>,
): ApiError | null {
  if (result.error) return toApiError(result.error);

  const session = result.data as { accessToken?: string; accessTokenExpiresAt?: string };
  if (typeof session.accessToken !== 'string' || typeof session.accessTokenExpiresAt !== 'string') {
    return sessionEndedError();
  }

  setAccessToken(session.accessToken, session.accessTokenExpiresAt);
  return null;
}

/**
 * Renew, then — only for `TOKEN_EXPIRED`, and only past this point — one channel-specific
 * recovery attempt. `null` means the caller may proceed or retry; anything else is the failure
 * to surface, and `onSessionEnded`/`onNeedsUnlock` has already been scheduled for it.
 * <para>
 * `TOKEN_EXPIRED` is the one code this is allowed to act on silently: it means the access token
 * itself had already expired when `/auth/renew` received it — a backgrounded tab woken up, not a
 * revocation (see `AuthenticationSetup.cs`'s `OnChallenge`). Every other renew failure —
 * `DEVICE_REVOKED`, `SIGNED_IN_ELSEWHERE`, `DEVICE_REVOKED_BY_OWNER`, `PIN_ATTEMPTS_EXCEEDED`,
 * `PASSWORD_CHANGED`, `REFRESH_EXPIRED` — is a real, specific reason the server has already
 * distinguished from plain expiry, and none of them are silently recovered from here.
 * </para>
 * <para>
 * The two recovery paths are never interchangeable. Dashboard falls back to `/auth/continue`
 * silently — no click, because this is a background request the person never saw fail, not the
 * boot-time "Continue as {name}?" screen, which stays a deliberate, visible tap for the case it
 * actually guards (see `ContinueSessionForm`'s doc comment). POS never calls `/auth/continue` —
 * see `useContinueSessionMutation`'s doc comment on why — and instead routes to the real PIN
 * screen via `onNeedsUnlock`, which leaves the session `locked` rather than wiped.
 * </para>
 */
async function recoverSession(
  inner: ReturnType<typeof raw>,
  api: Parameters<ReturnType<typeof raw>>[1],
  extraOptions: Parameters<ReturnType<typeof raw>>[2],
  channel: () => 'pos' | 'dashboard' | null,
  onSessionEnded: SessionEnded,
  onNeedsUnlock: () => void,
): Promise<ApiError | null> {
  const renewFailure = await refreshOnce(inner, api, extraOptions);
  if (renewFailure === null) return null;

  if (renewFailure.code === ErrorCode.RenewConflict) {
    // The loser of a concurrent renew — the winner's fresh token is already on the row. One
    // immediate retry, never treated as a session end.
    return refreshOnce(inner, api, extraOptions);
  }

  if (renewFailure.code !== ErrorCode.TokenExpired) {
    endSessionAfterThisRequestSettles(onSessionEnded, renewFailure.code, renewFailure.message);
    return renewFailure;
  }

  if (channel() === 'dashboard') {
    const continueFailure = await continueOnce(inner, api, extraOptions);
    if (continueFailure === null) return null;

    endSessionAfterThisRequestSettles(onSessionEnded, continueFailure.code, continueFailure.message);
    return continueFailure;
  }

  // POS (or a channel not yet configured — fails closed to the PIN screen, never to a silent
  // continue): the till itself must genuinely require the PIN to recover.
  onNeedsUnlock();
  return renewFailure;
}

const sessionEndedError = (): ApiError => ({
  status: 401,
  code: ErrorCode.DeviceRevoked,
  message: 'This device is no longer signed in. Sign in with your password to continue.',
});

/**
 * RFC 7807 in, `ApiError` out. Every branch produces a message that says what happened and what
 * to do next — the fallback is only reached when the response had no body at all.
 */
export function toApiError(error: FetchBaseQueryError): ApiError {
  if (error.status === 'FETCH_ERROR') {
    return {
      status: 0,
      code: ErrorCode.Offline,
      message: 'No connection to the server. The sale is saved on this device and will send when you are back online.',
    };
  }

  if (error.status === 'TIMEOUT_ERROR') {
    return {
      status: 0,
      code: ErrorCode.Offline,
      message: 'The server took too long to answer. Nothing was saved — try again.',
    };
  }

  if (error.status === 'PARSING_ERROR' || error.status === 'CUSTOM_ERROR') {
    return { status: 0, code: 'CLIENT_ERROR', message: FALLBACK_MESSAGE };
  }

  const problem = (error.data ?? {}) as ProblemDetails;

  return {
    status: error.status,
    code: problem.code ?? `HTTP_${error.status}`,
    message: problem.detail ?? problem.title ?? FALLBACK_MESSAGE,
    fields: problem.fields,
    problem,
  };
}
